import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  Country,
  PaymentGroup,
  PlanItem,
  PlanSimulation,
  PlanSimulationResponse,
  UserPackagePlan
} from 'src/models/register';
import { ApolloQueryResult } from '@apollo/client';
import { UserSubscriptionPackagePlan } from 'src/services/useBillingSystem';

interface Register {
  userPackagePlan: ApolloQueryResult<Array<UserPackagePlan>>;
  planSimulation: ApolloQueryResult<PlanSimulation>;
  countries: ApolloQueryResult<Array<Country>>;
  selectedCountryId: string;
  xenditPaymentChannel: ApolloQueryResult<Array<PaymentGroup>>;
  countryId: string;
  planSimulationV2: Pick<
    PlanSimulationResponse,
    | 'promoCodes'
    | 'planItems'
    | 'referralCode'
    | 'discount'
    | 'referralDiscount'
    | 'subTotal'
    | 'total'
    | 'promoType'
  >;
  email: string;
  packagePlan: UserSubscriptionPackagePlan | null;
}

const initialState: Register = {
  userPackagePlan: {
    data: [],
    loading: false,
    errors: [],
    networkStatus: 1,
    partial: false
  },
  planSimulation: {
    data: {
      isUsedPromo: false,
      isPromoValid: false,
      isLtd: false,
      promoErrorMessage: '',
      currency: 'idr',
      currencyBySelectedCountry: 'idr',
      quotaBasic: 0,
      quotaAdvance: 0,
      quotaOnlineMedia: 0,
      total: 0,
      totalBySelectedCountry: 0,
      period: 1,
      promoType: null,
      merchantName: 'xendit',
      promoCode: null,
      symbolBySelectedCountry: '',
      packagePlan: null
    },
    loading: false,
    errors: [],
    networkStatus: 1,
    partial: false
  },
  // used in register page
  planSimulationV2: {
    promoCodes: [],
    planItems: [],
    referralCode: '',
    discount: 0,
    referralDiscount: 0,
    subTotal: 0,
    total: 0,
    promoType: ''
  },
  countries: {
    data: [],
    loading: false,
    errors: [],
    networkStatus: 1,
    partial: false
  },
  selectedCountryId: '',
  xenditPaymentChannel: {
    data: [],
    loading: false,
    errors: [],
    networkStatus: 1,
    partial: false
  },
  countryId: '',
  // used in register page
  email: '',
  packagePlan: null
};

export const register = createSlice({
  name: 'register',
  initialState,
  reducers: {
    reducerUserPackagePlan: (
      state: Register,
      action: PayloadAction<ApolloQueryResult<Array<UserPackagePlan>>>
    ) => {
      state.userPackagePlan = action.payload;
    },
    reducerUserPlanSimulation: (
      state: Register,
      action: PayloadAction<ApolloQueryResult<PlanSimulation>>
    ) => {
      state.planSimulation = action.payload;
    },
    reducerCountries: (
      state: Register,
      action: PayloadAction<ApolloQueryResult<Array<Country>>>
    ) => {
      state.countries = action.payload;
    },
    reducerChangeSelectedCountryId: (
      state: Register,
      action: PayloadAction<string>
    ) => {
      state.selectedCountryId = action.payload;
    },
    reducerXenditPaymentChannel: (
      state: Register,
      action: PayloadAction<ApolloQueryResult<Array<PaymentGroup>>>
    ) => {
      state.xenditPaymentChannel = action.payload;
    },
    reducerUpdateCountryId: (
      state: Register,
      action: PayloadAction<string>
    ) => {
      state.countryId = action.payload;
    },
    reducerUpdateSimulationPromoCode: (
      state: Register,
      action: PayloadAction<string[]>
    ) => {
      state.planSimulationV2 = {
        ...state.planSimulationV2,
        promoCodes: action.payload
      };
    },
    reducerUpdateSimulationPlanItems: (
      state: Register,
      action: PayloadAction<PlanItem[]>
    ) => {
      state.planSimulationV2 = {
        ...state.planSimulationV2,
        planItems: action.payload
      };
    },
    reducerUpdateSimulationReferralCode: (
      state: Register,
      action: PayloadAction<string>
    ) => {
      state.planSimulationV2 = {
        ...state.planSimulationV2,
        referralCode: action.payload
      };
    },
    reducerUpdateEmail: (state: Register, action: PayloadAction<string>) => {
      state.email = action.payload;
    },
    reducerUpdatePackagePlan: (
      state: Register,
      action: PayloadAction<UserSubscriptionPackagePlan>
    ) => {
      state.packagePlan = action.payload;
    },
    reducerUpdatePrices: (
      state: Register,
      action: PayloadAction<{
        discount: number;
        referralDiscount: number;
        subTotal: number;
        total: number;
        promoType: string;
      }>
    ) => {
      state.planSimulationV2 = {
        ...state.planSimulationV2,
        ...action.payload
      };
    }
  }
});

export const {
  reducerUserPackagePlan,
  reducerUserPlanSimulation,
  reducerCountries,
  reducerChangeSelectedCountryId,
  reducerXenditPaymentChannel,
  reducerUpdateCountryId,
  reducerUpdateSimulationPromoCode,
  reducerUpdateSimulationPlanItems,
  reducerUpdateSimulationReferralCode,
  reducerUpdateEmail,
  reducerUpdatePrices,
  reducerUpdatePackagePlan
} = register.actions;

export default register.reducer;
